import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Experiments } from '@domain/configuration/experiments';
import { filterAndRankOrderEntryRows } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/utils/filter-and-rank-order-entry-rows';
import { Keyboard } from 'react-native';
import { OrderEntryCatalogSearch } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/hooks/use-order-entry-catalog-search';
import { OrderEntryMobileEmptyState } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-mobile-empty-state';
import { OrderEntryMobileItemDetail } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/containers/order-entry-mobile-item-detail';
import { OrderEntryMobileList } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-mobile-list';
import { OrderEntryMobileLoaderArgs } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/hooks/use-order-entry-mobile-data';
import { OrderEntryMode } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/types';
import { OrderEntryPriceEditDisplayHeader } from '@/types';
import { OrderEntryTableRowData } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/transformers/order-entry-table-transformers';
import {
  PermissionAction,
  PermissionSubject,
  SalesRepOverrideMode,
} from '@/enums';
import { useAuth } from '@/contexts/auth-context';
import { useCartStore } from '@/experiences/sales-portal/domain/ordering/blocs/stores/cart-store';
import { useCatalogSearchStore } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/stores/catalog-search-store';
import { useEmployeePermissionsLoader } from '@/domain/access-control/blocs/loaders/use-employee-permissions-loader';
import { useExperienceToasts } from '@experiences/hooks/use-experience-toasts';
import { useLastPurchaseInfoLoader } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/loaders/use-last-purchase-info-loader';
import { useOrderEntryReviewRows } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/hooks/use-order-entry-review-rows';
import { usePriceEditSave } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/hooks/use-price-edit-save';
import { useSalesPortalExperiment } from '@/experiences/sales-portal/blocs/hooks/use-sales-portal-experiment';
import { YStack } from '@usepepper/sprout-kit-ui';

const EMPTY_ROWS: OrderEntryTableRowData[] = [];

interface OrderEntryMobileListContainerProps {
  areSubstitutionsEnabled: boolean;
  bottomInset?: number;
  catalogSearch: OrderEntryCatalogSearch;
  debouncedSaveCart: (() => void) & { flush: () => void };
  fieldIds: string[];
  hasLoadError: boolean;
  isGrossProfitHidden: boolean;
  isLoading: boolean;
  isQuantityDisabled: boolean;
  labelsById: Record<string, string>;
  loaderArgs: OrderEntryMobileLoaderArgs;
  mode: OrderEntryMode;
  onRetryLoad: () => void;
  priceEditDisplayHeaders: OrderEntryPriceEditDisplayHeader[];
  showConfidentialFields: boolean;
  tableData: OrderEntryTableRowData[] | undefined;
}

export const OrderEntryMobileListContainer: React.FC<
  OrderEntryMobileListContainerProps
> = ({
  areSubstitutionsEnabled,
  bottomInset,
  catalogSearch,
  debouncedSaveCart,
  fieldIds,
  hasLoadError,
  isGrossProfitHidden,
  isLoading,
  isQuantityDisabled,
  labelsById,
  loaderArgs,
  mode,
  onRetryLoad,
  priceEditDisplayHeaders,
  showConfidentialFields,
  tableData,
}) => {
  const {
    chatUUID,
    deliveryDate,
    restaurantTimezone,
    restaurantUUID,
    salesRepConfig,
    supplierUUID,
  } = loaderArgs;

  const { showErrorToast } = useExperienceToasts();
  const authenticatedUserUUID = useAuth((state) => state.authenticatedUserUUID);
  const { can } = useEmployeePermissionsLoader(
    supplierUUID,
    authenticatedUserUUID,
  );
  const { enabled: isPriceEditingExperimentEnabled } = useSalesPortalExperiment(
    {
      experimentKey: Experiments.ORDER_ENTRY_PRICE_EDITING,
      supplierUUID,
    },
  );

  const isPriceEditingEnabled =
    isPriceEditingExperimentEnabled &&
    can(PermissionAction.Write, PermissionSubject.SrtPriceEditing) &&
    salesRepConfig !== null &&
    salesRepConfig.override_mode !== SalesRepOverrideMode.DISABLED &&
    !salesRepConfig.disable_override_editing;
  const showPriceOverrideInfo =
    isPriceEditingExperimentEnabled &&
    can(PermissionAction.Read, PermissionSubject.SrtPriceEditing);
  const canSetManualOverrideExpiration =
    showPriceOverrideInfo &&
    !!salesRepConfig?.can_set_manual_override_expiration;

  const { handleSave: handleSavePriceEdit } = usePriceEditSave({
    deliveryDate,
    onSaveError: showErrorToast,
    overrideMode:
      salesRepConfig?.override_mode ?? SalesRepOverrideMode.DISABLED,
    restaurantUUID,
    salesRepConfig,
    supplierUUID,
  });

  const { lastPurchaseInfoMap } = useLastPurchaseInfoLoader(
    chatUUID,
    restaurantTimezone,
  );

  const buildRows = tableData ?? EMPTY_ROWS;

  useEffect(() => () => debouncedSaveCart.flush(), [debouncedSaveCart]);
  const onQuantityChange = useCallback(
    (variantPackUUID: string, quantity: number) => {
      if (
        useCartStore
          .getState()
          .updateCartItemQuantity(variantPackUUID, quantity)
      ) {
        debouncedSaveCart();
      }
    },
    [debouncedSaveCart],
  );

  const reviewRows = useOrderEntryReviewRows(mode, buildRows);
  const isReviewMode = mode === OrderEntryMode.Review;

  const [detailVariantPackUUID, setDetailVariantPackUUID] = useState<
    string | null
  >(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const handleCloseDetail = useCallback(() => setIsDetailOpen(false), []);
  const dismissDetail = useCallback(() => {
    setIsDetailOpen(false);
    setDetailVariantPackUUID(null);
  }, []);

  const {
    catalogErrorCode,
    catalogResults,
    catalogSearchTerm,
    handleRetryCatalogSearch,
    isCatalogSearching,
    isInCatalogMode,
    searchTerm,
    showCatalogEmpty,
    showCatalogError,
    showCatalogResults,
  } = catalogSearch;

  useEffect(
    () =>
      useCatalogSearchStore.subscribe(({ mode }, previous) => {
        if (mode !== previous.mode) {
          dismissDetail();
        }
      }),
    [dismissDetail],
  );

  const modeRows = isReviewMode ? reviewRows : buildRows;
  const guideRows = useMemo(
    () => filterAndRankOrderEntryRows(modeRows, searchTerm),
    [modeRows, searchTerm],
  );
  const visibleRows = isInCatalogMode
    ? showCatalogResults
      ? catalogResults
      : EMPTY_ROWS
    : guideRows;
  const hasNoSearchMatches =
    !isInCatalogMode &&
    searchTerm.trim().length > 0 &&
    guideRows.length === 0 &&
    modeRows.length > 0;

  const handleOpenDetail = useCallback((variantPackUUID: string) => {
    Keyboard.dismiss();
    setDetailVariantPackUUID(variantPackUUID);
    setIsDetailOpen(true);
  }, []);
  const detailRow = detailVariantPackUUID
    ? (isInCatalogMode ? catalogResults : modeRows).find(
        (row) => row.variantPackUUID === detailVariantPackUUID,
      )
    : undefined;

  const emptyComponent = (
    <OrderEntryMobileEmptyState
      catalogErrorCode={catalogErrorCode}
      catalogSearchTerm={catalogSearchTerm}
      hasLoadError={hasLoadError}
      hasNoSearchMatches={hasNoSearchMatches}
      isLoading={isLoading || isCatalogSearching}
      isReviewMode={isReviewMode}
      onRetryCatalogSearch={handleRetryCatalogSearch}
      onRetryLoad={onRetryLoad}
      searchTerm={searchTerm}
      showCatalogEmpty={showCatalogEmpty}
      showCatalogError={showCatalogError}
    />
  );

  return (
    <YStack flex={1}>
      <OrderEntryMobileList
        areSubstitutionsEnabled={areSubstitutionsEnabled}
        bottomInset={bottomInset}
        data={visibleRows}
        emptyComponent={emptyComponent}
        fieldIds={fieldIds}
        isGrossProfitHidden={isGrossProfitHidden}
        isQuantityDisabled={isQuantityDisabled || isLoading}
        labelsById={labelsById}
        lastPurchaseInfoMap={lastPurchaseInfoMap}
        onOpenDetail={handleOpenDetail}
        onQuantityChange={onQuantityChange}
        priceEditDisplayHeaders={priceEditDisplayHeaders}
        restaurantTimezone={restaurantTimezone}
        selectedCollectionId={loaderArgs.orderGuideUUID}
        showConfidentialFields={showConfidentialFields}
      />
      {detailVariantPackUUID !== null ? (
        <OrderEntryMobileItemDetail
          canSetManualOverrideExpiration={canSetManualOverrideExpiration}
          deliveryDate={deliveryDate}
          isGrossProfitHidden={isGrossProfitHidden}
          isPriceEditingEnabled={isPriceEditingEnabled}
          onClose={handleCloseDetail}
          onSavePriceEdit={handleSavePriceEdit}
          open={isDetailOpen}
          restaurantTimezone={restaurantTimezone}
          restaurantUUID={restaurantUUID}
          row={detailRow}
          showPriceOverrideInfo={showPriceOverrideInfo}
          supplierUUID={supplierUUID}
          variantPackUUID={detailVariantPackUUID}
        />
      ) : null}
    </YStack>
  );
};
